import React, { useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import { Application } from "../../model_gen/application";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { X, Plus } from "lucide-react";

interface NewApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplicationCreated: (application: Application) => void;
}

const NewApplicationModal: React.FC<NewApplicationModalProps> = ({
  isOpen,
  onClose,
  onApplicationCreated
}) => {
  const { apiKey } = usePermissions();
  const [formData, setFormData] = useState({
    application_name: "",
    description: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!apiKey || !formData.application_name.trim()) {
      setError("Application name is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    const applicationData = {
      application_name: formData.application_name.trim(),
      description: formData.description.trim() || null,
    };

    ApplicationService.registerApplication(applicationData, setIsLoading, apiKey).subscribe(
      (response: Application) => {
        setIsLoading(false);
        onApplicationCreated(response);
        handleClose();
      },
      (error: any) => {
        console.error("Error creating application:", error);
        setIsLoading(false);
        setError(error.message || "Failed to create application");
      }
    );
  };

  const handleClose = () => {
    setFormData({
      application_name: "",
      description: ""
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-lg max-w-md w-full mx-4">
        <Card className="border-0 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-gc-med">Create New Application</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="app_name">Application Name</Label>
                <Input
                  id="app_name"
                  type="text"
                  placeholder="e.g., Customer Support, Sales Assistant, etc."
                  value={formData.application_name}
                  onChange={(e) => handleInputChange("application_name", e.target.value)}
                  className="w-full"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="app_description">Description (Optional)</Label>
                <Textarea
                  id="app_description"
                  placeholder="Brief description of what this application does..."
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="w-full min-h-[80px]"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!formData.application_name.trim() || isLoading}
                  className="flex-1 gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {isLoading ? "Creating..." : "Create Application"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewApplicationModal; 